import * as L from "leaflet";
import { Settings } from "./settings/Settings";
import { ControlManager } from "./control/ControlManager";
import { PlayerManager } from "./player/PlayerManager";
import { WorldManager } from "./world/WorldManager";
import { getJSON } from "./util/Util";
import SidebarControl from "./control/SidebarControl";
import { MarkerPlacementControl } from "./control/MarkerPlacementControl";
import { MarkerToggleControl } from "./control/MarkerToggleControl";
import Pl3xMapLeafletMap from "./map/Pl3xMapLeafletMap";
import "./scss/styles.scss";
import { Player } from "./player/Player";

window.onload = function (): void {
    window.pl3xmap = new Pl3xMap();
};

/**
 * Represents the main Pl3xMap class.
 */
export class Pl3xMap {
    private static _instance: Pl3xMap;

    private readonly _map: Pl3xMapLeafletMap;

    private readonly _controlManager: ControlManager;
    private readonly _playerManager: PlayerManager;
    private readonly _worldManager: WorldManager;

    private _eventSource?: EventSource;

    private _langPalette: Map<string, string> = new Map();
    private _settings?: Settings;

    private _timestamp: number = new Date().getTime();
    private _timer: NodeJS.Timeout | undefined;

    private _authToken?: string;
    private readonly AUTH_TOKEN_KEY = "pl3xmap_auth_token";

    constructor() {
        Pl3xMap._instance = this;

        // Load auth token from localStorage
        this.loadAuthToken();

        this._map = new Pl3xMapLeafletMap(this);

        window.addEventListener("beforeunload", function () {
            if (Pl3xMap.instance.eventSource != undefined) {
                Pl3xMap.instance.eventSource.close();
            }
        });

        // update map size when window size, scale, or orientation changes
        "orientationchange resize".split(" ").forEach((event: string): void => {
            window.addEventListener(
                event,
                (): void => {
                    this._map.updateSizeToWindow();
                },
                { passive: true },
            );
        });

        this._controlManager = new ControlManager(this);
        this._playerManager = new PlayerManager(this);
        this._worldManager = new WorldManager(this);

        getJSON("tiles/settings.json").then((json) => {
            this._settings = json as Settings;
            document.title = this._settings.lang.title;

            // these get weird when changed. so don't.
            this.map.options.zoomSnap = 1;
            this.map.options.zoomDelta = 1;

            // chrome based browsers on linux zoom twice as fast, so we have to double the ratio
            // effectively undoes the fix for Leaflet/Leaflet#4538 and Leaflet/Leaflet#7403
            // https://github.com/Leaflet/Leaflet/commit/96977a19358374b0166cff049862fa1f0fed5948
            //
            // remove this logic when this bug gets fixed: https://issues.chromium.org/issues/40887377
            // it seems intentional, so it might not get fixed https://issues.chromium.org/issues/40804672
            this.map.options.wheelPxPerZoomLevel = L.Browser.linux && L.Browser.chrome ? 120 : 60;

            getJSON("lang/" + this._settings.lang.langFile).then((json): void => {
                Object.entries(json).forEach((data: [string, unknown]): void => {
                    this._langPalette.set(data[0], <string>data[1]);
                });
            });
            const sidebar: SidebarControl = new SidebarControl(this);
            this.controlManager.sidebarControl = sidebar;
            this.controlManager.markerPlacementControl = new MarkerPlacementControl(this, sidebar);
            // Sits with the zoom bar; not world-specific, so it is created once.
            this.controlManager.markerToggleControl = new MarkerToggleControl(this, "topleft");
            const promise: Promise<void> = this.worldManager.init(this._settings);
            this._eventSource = this.initSSE();
            this.update();
            return promise;
        });
    }

    public static get instance(): Pl3xMap {
        return Pl3xMap._instance;
    }

    private update(): void {
        if (this._eventSource?.readyState === EventSource.OPEN && new Date().getTime() - this._timestamp < 1000) {
            this._timer = setTimeout(() => this.update(), 1000);
            return;
        }
        getJSON("tiles/settings.json").then((json): void => {
            this._settings = json as Settings;

            this.playerManager.update(this._settings.players);

            this._timer = setTimeout(() => this.update(), 1000);
        });
    }

    private initSSE(): EventSource {
        const eventSource = new EventSource("sse");

        eventSource.addEventListener("settings", (ev: Event) => {
            this._timestamp = new Date().getTime();
            const messageEvent = ev as MessageEvent;
            const json: any = JSON.parse(messageEvent.data);
            this._settings = json as Settings;
            this.playerManager.update(this._settings.players);
        });

        return eventSource;
    }

    get map(): Pl3xMapLeafletMap {
        return this._map;
    }

    get controlManager(): ControlManager {
        return this._controlManager;
    }

    get playerManager(): PlayerManager {
        return this._playerManager;
    }

    get worldManager(): WorldManager {
        return this._worldManager;
    }

    get eventSource(): EventSource | undefined {
        return this._eventSource;
    }

    get langPalette(): Map<string, string> {
        return this._langPalette;
    }

    get settings(): Settings | undefined {
        return this._settings;
    }

    get timestamp(): number {
        return this._timestamp;
    }

    /**
     * Load auth token from localStorage
     */
    private loadAuthToken(): void {
        try {
            const token = localStorage.getItem(this.AUTH_TOKEN_KEY);
            if (token) {
                this._authToken = token;
                console.log("✅ Auth token loaded from localStorage");
            } else {
                console.log("ℹ️ No auth token found in localStorage");
            }
        } catch (e) {
            console.warn("Failed to load auth token:", e);
        }
    }

    /**
     * Set auth token and save to localStorage
     */
    public setAuthToken(token: string): void {
        this._authToken = token;
        try {
            localStorage.setItem(this.AUTH_TOKEN_KEY, token);
            console.log("✅ Auth token saved to localStorage");
        } catch (e) {
            console.warn("Failed to save auth token:", e);
        }
    }

    /**
     * Get current auth token
     */
    public getAuthToken(): string | undefined {
        return this._authToken;
    }

    /**
     * Clear auth token
     */
    public clearAuthToken(): void {
        this._authToken = undefined;
        try {
            localStorage.removeItem(this.AUTH_TOKEN_KEY);
            console.log("✅ Auth token cleared");
        } catch (e) {
            console.warn("Failed to clear auth token:", e);
        }
    }

    /**
     * Check if user is authenticated
     */
    public isAuthenticated(): boolean {
        return !!this._authToken;
    }

    /**
     * Make authenticated API request
     */
    public async makeAuthRequest(url: string, options: RequestInit = {}): Promise<Response> {
        if (!this._authToken) {
            throw new Error("No auth token available. Use pl3xmap.setAuthToken(token) first.");
        }

        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${this._authToken}`,
                "Content-Type": "application/json",
            },
        });
    }
}
